import { useEffect, useMemo, useRef, useState } from "react";
import ResponseUtil from "@plannel/pages/utils/response-util";
import { AgGridReact } from "ag-grid-react";
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Box from "@mui/material/Box"

import { useDispatch } from "react-redux";
import { withTranslation } from "react-i18next";
import { isEmpty, isEqual, isNull, isUndefined } from "lodash";

import alternateSupplyService from "@plannel/services/data/alternate-supply-service";
import locationService from "@plannel/services/data/location-service";
import routeService from "@plannel/services/data/route-service";
import PaginationContainer from "@plannel/components/PaginationContainer";
import Dialog from "@plannel/components/Dialog";
import ExcelExportButton from "@plannel/components/ExcelExportButton";
import ItemAutocomplete from "@plannel/components/filter/ItemAutocomplete";
import Snackbar from "@plannel/components/Snackbar";
import DataState from "@plannel/components/aggrid/DataState";
import DefaultGridSetting, { gridPaste, nullSafeComparator } from "@plannel/components/aggrid/DefaultGridSetting";
import { AddButton, RemoveButton, SaveButton, FilterButton } from "@plannel/components/ActionIconButton";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import AdvancedFilter from "@plannel/components/filter/AdvancedFilter";
import ResizableFilterPanel from "@plannel/components/filter/ResizableFilterPanel";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import reduxUtil from "@plannel/utils/redux-util";
import debug from "@plannel/utils/debug-util";
import dateUtils from "@plannel/utils/date-util";
import ColorUtil from "../utils/color-util";

const { snackBarMessageColor } = ColorUtil;
const PLANT = "PLANT";
const { httpStatusCode, responseCode } = ResponseUtil;

const AlternateSupply = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const reduxState = reduxUtil.createObjectState(viewName);
  reduxDispatch(reduxUtil.updateViewState({ ...reduxState }));

  const pageSizes = [100, 200, 500];
  const [currentPage, setCurrentPage] = useState(reduxViewState ? reduxViewState.currentPage : 1);
  const [tPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(reduxViewState ? reduxViewState.pageSize : pageSizes[0]);
  const [sortParams, setSortParams] = useState({});

  const [locationSearch, setLocationSearch] = useState(reduxViewState?.locationSearch ?? null);
  const itemSearch = useRef(reduxViewState?.itemSearch ?? "");
  const extraItemCodesRef = useRef(reduxViewState?.extraItemCodes ?? []);
  const [routeSearch, setRouteSearch] = useState(reduxViewState?.routeSearch ?? null);
  const [advancedFilters, setAdvancedFilters] = useState(reduxViewState?.advancedFilters ? reduxViewState.advancedFilters : null);
  const [openFilter, setOpenFilter] = useState(false);
  const [advancedFilterColor, setAdvancedFilterColor] = useState(!isEmpty(reduxViewState?.advancedFilters) ? "info" : "default");

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });

  const gridRef = useRef();
  const [gridLoading, setGridLoading] = useState(false);

  const [altSupplies, setAltSupplies] = useState([]);
  const [locations, setLocations] = useState([]);
  const [locationPlant, setLocationPlant] = useState([]);
  const [routes, setRoutes] = useState([]);
  const routeMap = useRef({});

  const defaultGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dropdownValueFormatter = (data) => {
    return data.value ? data.value.name : "";
  }

  const objectComparator = (value1, value2, isDescending) => {
    const val1 = !value1 ? "" : value1.name;
    const val2 = !value2 ? "" : value2.name;
    
    return nullSafeComparator(val1, val2, isDescending);
  };

  const checkInputValidation = (params) => {
    const colId = params.column.colId;

    if (isNaN(params.newValue) || isNull(params.newValue) || isUndefined(params.newValue) || params.newValue === "") {
      params.data[colId] = "";
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_InvalidValue") });
      return false;
    }

    if (params.newValue < 0) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_NotEnterNegativeValue") });
      return false;
    }

    if (!Number.isInteger(Number(params.newValue))) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_InvalidValue") });
      return false;
    }

    params.data[colId] = params.newValue;

    return true;
  }

  const onCellValueChanged = (params) => {
    gridPaste(params);

    const { column, node } = params;
    const colId = column.getColId();

    if (isEqual(colId, "locName")) {
      params.api.refreshCells({ rowNodes: [node], columns: ["locCd"], force: true });
    } else if (isEqual(colId, "consmpRouteCd")) {
      const selected = routeMap.current[params.value];

      node.data.consmpRouteId = selected ? selected.id : null;
      node.data.consmpRouteName = selected ? selected.name : null;

      params.api.refreshCells({ rowNodes: [node], columns: ["consmpRouteName"], force: true });
    }

    DataState.setDataState(params);
  }

  const columnDefs = [
    { headerName: "locCd", field: "location.locCd", colId: "locCd", minWidth: 150, type: ["nonEditableColumn"], flex: 2, cellClass: 'stringType', filterType: "string" },
    {
      headerName: "locName", field: "location", colId: "locName",
      cellEditor: "agRichSelectCellEditor",
      cellEditorPopup: true,
      type: ["defaultValueParser"],
      valueFormatter: dropdownValueFormatter,
      cellEditorParams: {
        formatValue: (value) => value ? value.name : "",
        values: locationPlant
      },
      comparator: (value1, value2, nodeA, nodeB, isDescending) => objectComparator(value1, value2, isDescending),
      minWidth: 200,
      flex: 2,
      filterType: "string",
    },
    {
      headerName: "itemCd", field: "itemCd", colId: "itemCd",
      cellClass: 'stringType',
      width: 150, filterType: 'string'
    },
    {
      headerName: "itemName", field: "itemName", colId: "itemName",
      type: ["nonEditableColumn"],
      width: 280, filterType: 'string'
    },
    {
      headerName: "altItemCd", field: "altItemCd", colId: "altItemCd",
      cellClass: 'stringType',
      width: 150, filterType: 'string'
    },
    {
      headerName: "altItemName", field: "altItemName", colId: "altItemName",
      type: ["nonEditableColumn"],
      width: 280, filterType: 'string'
    },
    {
      headerName: "consmpRouteCd", field: "consmpRouteCd", colId: "consmpRouteCd",
      cellEditor: "agRichSelectCellEditor",
      cellEditorPopup: true,
      type: ["defaultValueParser"],
      cellEditorParams: {
        values: routes
      },
      comparator: (value1, value2, nodeA, nodeB, isDescending) => nullSafeComparator(value1, value2, isDescending),
      cellClass: 'stringType',
      width: 150, filterType: 'string'
    },
    {
      headerName: "consmpRouteName", field: "consmpRouteName", colId: "consmpRouteName",
      type: ["nonEditableColumn"],
      width: 180, filterType: 'string'
    },
    {
      headerName: "priority", field: "priority", colId: "priority",
      type: ["numberColumn", "rightAligned"],
      valueSetter: (params) => checkInputValidation(params),
      width: 120, filterType: 'number'
    },
    { headerName: "activeFlg", field: "activeFlg", type: ["booleanColumn"], width: 100, filterType: 'boolean' },
    { headerName: "createdTs", field: "createdTs", type: ["nonEditableColumn"], valueFormatter: dateUtils.formatDateTime, filterType: 'timestamp' },
    { headerName: "createdBy", field: "createdUser", colId: "createdBy", type: "nonEditableColumn", width: 150, filterType: 'string' },
    { headerName: "updatedTs", field: "updatedTs", type: ["nonEditableColumn"], valueFormatter: dateUtils.formatDateTime, filterType: 'timestamp' },
    { headerName: "updatedBy", field: "updatedUser", colId: "updatedBy", type: "nonEditableColumn", width: 150, filterType: 'string' },
    { field: "verNum", hide: true }
  ];

  const getRequestParams = () => {
    const params = {};
    const searchFilters = {};

    if (locationSearch) {
      searchFilters.locCd = locationSearch.locCd;
    }

    if (extraItemCodesRef.current?.length > 0) {
      searchFilters.items = [itemSearch.current, ...extraItemCodesRef.current].filter(Boolean);
    } else if (itemSearch.current) {
      searchFilters.item = itemSearch.current;
    }

    if (routeSearch) {
      searchFilters.consmpRouteCd = routeSearch;
    }

    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      params.advancedFilters = advancedFilters;
    }

    if (currentPage) {
      params.page = currentPage - 1;
    }

    if (pageSize) {
      params.pageSize = pageSize;
    }

    params.searchFilters = searchFilters;

    return { ...params, ...sortParams };
  }

  const retrieveAltSupply = () => {
    const params = getRequestParams();

    alternateSupplyService.getAll(params)
      .then(response => {
        const { results, totalPages } = response.data;

        if (isEmpty(results)) {
          setAltSupplies([]);
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        }

        setAltSupplies(results);
        setTotalPages(totalPages);
      })
      .catch(e => {
        debug(e);
      })
      .then(() => {
        const reduxState = reduxUtil.createObjectState(viewName, {
          locationSearch, itemSearch: itemSearch.current, extraItemCodes: extraItemCodesRef.current,
          routeSearch, currentPage, pageSize, advancedFilters
        });

        reduxDispatch(reduxUtil.updateViewState(reduxState));
        DataState.initialize(gridRef.current?.api);
      });
  }

  const retrieveLocation = () => {
    locationService.getLookup({ isLocationMaster: true })
      .then(response => {
        const { results } = response.data;
        const plant = [];

        for (const loc of results) {
          if (loc.locType === PLANT) {
            plant.push(loc);
          }
        }
        
        setLocationPlant(plant);
        setLocations(results);
        if (reduxViewState) {
          reduxViewState.locations = results;
          reduxViewState.locationPlant = plant;
          reduxDispatch(reduxUtil.updateViewState({ ...reduxState, ...reduxViewState }));
        }
      })
      .catch(e => {
        debug(e);
      })
  };

  const retrieveRoute = () => {
    routeService.getLookup()
      .then(response => {
        const { data } = response;
        const routes = [];
        for (const route of data ?? []) {
          routes.push(route.routeCd);
          routeMap.current[route.routeCd] = route;
        }
        setRoutes(routes);
      })
      .catch(e => {
        debug(e);
      });
  }

  const onGridReady = params => {
    retrieveLocation();
    retrieveRoute();
    setGridLoading(true);
    GridUtils.setColumnDefs({ ...params, columnDefs, viewName, initState: true });
  }

  const addRow = () => {
    const newRow = {
      id: null,
      location: null,
      itemId: null,
      itemCd: null,
      itemName: null,
      consmpRouteId: null,
      consmpRouteCd: null,
      consmpRouteName: null,
      altItemId: null,
      altItemCd: null,
      altItemName: null,
      priority: 999,
      activeFlg: true,
      verNum: null
    };

    DataState.setDataState(GridUtils.addRow(gridRef.current.api, newRow));
  }

  const removeAltSupply = () => {
    const altSupplyIds = [];

    gridRef.current.api.stopEditing();
    gridRef.current.api.getSelectedRows().forEach(
      (data) => {
        altSupplyIds.push(data.id)
      }
    );

    if (altSupplyIds.length > 0) {
      alternateSupplyService.remove(altSupplyIds)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_DeleteSuccess") });
        })
        .catch(e => {
          const { message } = e.response?.data ?? {};
          if (message) {
            setSnackInfo({ open: true, severity: message.type, content: t(message.message) });
          } else {
            setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_DeleteFailed") });
          }
          debug(e);
        })
        .then(() => {
          retrieveAltSupply();
        });
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  }

  const saveAltSupply = () => {
    gridRef.current.api.stopEditing();
    const allStateData = DataState.getAllStateData(gridRef.current.api);

    if (allStateData.length === 0) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
      return;
    }

    for (const row of allStateData) {
      if (isEmpty(row.location)) {
        setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: `[${t("locName")}] ${t("MSG_FieldRequired")}` });
        return;
      }

      if (isEmpty(row.itemCd)) {
        setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: `[${t("itemCd")}] ${t("MSG_FieldRequired")}` });
        return;
      }

      if (isEmpty(row.altItemCd)) {
        setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: `[${t("altItemCd")}] ${t("MSG_FieldRequired")}` });
        return;
      }

      if (isEqual(row.itemCd, row.altItemCd)) {
        setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_ItemAltItemNotSame") });
        return;
      }

      if (isNull(row.priority) || row.priority === "") {
        setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: `[${t("priority")}] ${t("MSG_FieldRequired")}` });
        return;
      }

      /* 소요 Route는 선택값 — 비우면 null로 저장하고, 입력된 경우에만 마스터 존재 여부를 확인한다. */
      if (isEmpty(row.consmpRouteCd)) {
        row.consmpRouteId = null;
      } else {
        row.consmpRouteId = routeMap.current[row.consmpRouteCd]?.id;
        if (!row.consmpRouteId) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: `[${t("consmpRouteCd")}] ${t("MSG_InvalidValue")}` });
          return;
        }
      }
    }

    alternateSupplyService.upsert(allStateData)
      .then(() => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
      })
      .catch(e => {
        const status = e?.response?.status;
        const errorDatas = e?.response?.data ?? {};
        const { message } = errorDatas;

        if (status === httpStatusCode.BadRequest) {
          for (const errorCode of Object.keys(errorDatas)) {
            if (errorCode === responseCode.NOT_FOUND_ITEM) {
              setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_NotExistItemCodes", { itemCodes: errorDatas[errorCode] }) });
            } else {
              setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
            }
            break;
          }
        } else if (message) {
          setSnackInfo({ open: true, severity: message.type, content: t(message.message) });
        } else {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
        }
        debug(e);
      })
      .then(() => {
        retrieveAltSupply();
      });
  }

  const handleDialog = (action) => {
    if (action === "SAVE") {
      saveAltSupply();
    } else if (action === "DELETE") {
      removeAltSupply();
    }
  }

  const initPageToRetrieve = () => {
    if (currentPage > 1) {
      setCurrentPage(1);
    } else {
      retrieveAltSupply();
    }
  }

  const collapseFilter = _event => {
    setOpenFilter(!openFilter);
  };

  useEffect(() => {
    if (!isEmpty(locations)) {
      GridUtils.setColumnDefs({ ...gridRef.current, columnDefs, viewName });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locations]);

  useEffect(() => {
    if (!isEmpty(routes)) {
      GridUtils.setColumnDefs({ ...gridRef.current, columnDefs, viewName });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routes]);

  useEffect(() => {
    if (gridLoading) {
      DataState.initialize(gridRef.current?.api);
      retrieveAltSupply();
    }

    return () => {
      setAltSupplies([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, gridLoading, sortParams]);

  useEffect(() => {
    if (gridLoading) {
      initPageToRetrieve();
    }

    return () => {
      setAltSupplies([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters, locationSearch, routeSearch]);

  useEffect(() => {
    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      setAdvancedFilterColor("info");
    } else {
      setAdvancedFilterColor("default");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters]);

  return (
    <>
      <FilterContainer>
        <Autocomplete
          options={locationPlant}
          getOptionLabel={option => option.name ?? ""}
          value={locationSearch}
          onChange={(_, newValue) => setLocationSearch(newValue)}
          isOptionEqualToValue={(option, value) => option.locCd === value.locCd}
          size="small"
          sx={{ pr: 1, minWidth: 240 }}
          renderInput={(params) => (
            <TextField {...params} label={t("location")} />
          )}
        />
        <ItemAutocomplete retrieve={initPageToRetrieve} searchValue={itemSearch} useAccessFilter={false} extraCodesRef={extraItemCodesRef} />
        <Autocomplete
          options={routes}
          getOptionLabel={option => option}
          value={routeSearch}
          onChange={(_, newValue) => setRouteSearch(newValue)}
          isOptionEqualToValue={(option, value) => option === value}
          size="small"
          sx={{ pr: 1, minWidth: 200 }}
          renderInput={(params) => (
            <TextField {...params} label={t("consmpRouteCd")} />
          )}
        />
        <Box sx={{ ml: "auto", mr: 1 }}>
          <ExcelExportButton title={title} service={alternateSupplyService} params={getRequestParams()} columnDefs={columnDefs} />
          <AddButton onClick={addRow} />
          <RemoveButton onClick={() => setDialogInfo({ open: true, title: "delete", content: t("MSG_DeleteConfirm") })} />
          <SaveButton onClick={() => setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm") })} />
          <FilterButton label="advancedFilter" advancedFilterColor={advancedFilterColor} onClick={collapseFilter} />
        </Box>
      </FilterContainer>
      <Box id="alternate-supply-grid" className="ag-theme-balham grid-area" mb={1}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'row', height: '100%', mr: 1.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <AgGridReact
              ref={gridRef}
              rowData={altSupplies}
              {...defaultGridMemo}
              rowSelection={{
                ...defaultGridMemo.rowSelection,
                headerCheckbox: true,
                checkboxes: true,
              }}
              onGridReady={onGridReady}
              onCellValueChanged={onCellValueChanged}
              onSortChanged={params => setSortParams(GridUtils.getSortState(params))}
            />
          </Box>
          {openFilter && (
            <ResizableFilterPanel>
              <AdvancedFilter
                columnDefs={columnDefs}
                advancedFilters={advancedFilters}
                onApply={setAdvancedFilters}
                onClose={() => setOpenFilter(false)}
              />
            </ResizableFilterPanel>
          )}
        </Box>
      </Box>
      <PaginationContainer
        currentPage={currentPage} totalPages={tPages}
        pageSize={pageSize} pageSizes={pageSizes}
        setCurrentPage={setCurrentPage} setPageSize={setPageSize}
      />
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(AlternateSupply);
